import groupBy from 'lodash-es/groupBy'
import map from 'lodash-es/map'

import type { Offer } from '@fourthwall/services/dist/models/offer'
import type { VariantType } from '@fourthwall/services/dist/models/variantType'

import type { ProductFormState } from '../../SingleProduct.types'
import type { Dictionary } from '../../SingleProduct.utils'
import type { VariantItem } from './types'

export const getVariantTypesWithVaryingField = (types: VariantType[], fieldName: string) => {
  return types.filter((variantType) => (variantType.variesBy as any)[fieldName])
}

export const getAttributeByType = (variant: Offer.Variant, type: VariantType['type']) => {
  return variant.attributesList.find((attribute) => attribute.type === type)
}

const getGroupLabel = (variant: Offer.Variant, variantsAttributesValues: string[], glue = ',') => {
  return variantsAttributesValues.join(glue)
}

const getVariantTags = (variant: any, variantTypes: VariantType[]) => {
  return variantTypes.map((variantType: VariantType) => {
    const attribute = getAttributeByType(variant, variantType.type)

    return {
      type: variantType.type,
      name: attribute?.name,
      colorSwatch: attribute?.colorSwatch,
    }
  })
}

const getVariantAttributesValues = (variant: Offer.Variant, variantTypes: VariantType[]) => {
  return variantTypes
    .map((variantType) => getAttributeByType(variant, variantType.type)?.name)
    .filter(Boolean) as string[]
}

const addVariantsProps = (
  variantGroups: Dictionary<Offer.Variant[]>,
  variantTypes: VariantType[],
) => {
  return map(variantGroups, (variantGroup, key) =>
    variantGroup.map((variant, index) => {
      const variantAttributesValues = getVariantAttributesValues(variant, variantTypes)

      return {
        ...variant,
        tags: getVariantTags(variant, variantTypes),
        groupId: getGroupLabel(variant, variantAttributesValues),
        groupLabel: getGroupLabel(variant, variantAttributesValues, ', '),
        isFirstOfGroup: index === 0,
        isLastOfGroup: index === variantGroups[key].length - 1,
      }
    }),
  )
}

export const getVariantsGroups = (variants: Offer.Variant[], variantTypes: VariantType[]) => {
  const variantTypesWithVaryingImages = getVariantTypesWithVaryingField(variantTypes, 'imagery')

  const variantGroups = groupBy(variants, (variant) => {
    const variantAttributesValues = getVariantAttributesValues(
      variant,
      variantTypesWithVaryingImages,
    )

    return getGroupLabel(variant, variantAttributesValues)
  })

  return addVariantsProps(variantGroups, variantTypesWithVaryingImages)
}

export const flattenVariantGroups = (variantGroups: any) => {
  return Object.keys(variantGroups).reduce(
    (result: VariantItem[], key) => [...result, ...variantGroups[key]],
    [],
  )
}

export const updateValuesVariantQuantity = (
  values: ProductFormState,
  variantId: string,
  quantity: number | undefined,
) => {
  return {
    ...values,
    variants: values.variants.map((variant) => {
      return {
        ...variant,
        quantity: variant.id === variantId ? quantity : variant.quantity,
      }
    }),
  }
}

export const getPreviewImageUrl = (imageIds: any, allImages: any) => {
  if (!imageIds.length) return undefined

  return allImages ? allImages.find((image: any) => image.id === imageIds[0])?.url : undefined
}
